import type { FC } from "react";
import styled from "@emotion/styled";
import { ArrowRight } from "lucide-react";
import {
  breakpoints,
  colors,
  radius,
  spacing,
} from "../../design-tokens";
import { QuestionText } from "../../primitives/typography";
import type { FollowUpQuestion } from "@/types/chat-parts";

const QuestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.lg}px;
  width: 100%;

  @media (max-width: ${breakpoints.md - 1}px) {
    grid-template-columns: repeat(1, 1fr);
    gap: ${spacing.md}px;
  }
`;

const QuestionCard = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${spacing.xl}px;
  background: ${colors.background};
  border: none;
  padding: ${spacing.lg}px;
  border-radius: ${radius.md}px;
  cursor: pointer;
  min-height: 100px;
  text-align: left;
  box-sizing: border-box;
  transition: background-color 200ms ease;

  // Reset button styles and prevent text selection
  outline: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &:hover {
    background: ${colors.cardHover};
  }

  &:active {
    background: ${colors.cardActive};
  }

  &:focus-visible {
    outline: 2px solid ${colors.accent};
    outline-offset: 2px;
  }
`;

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${colors.iconAccent};

  svg {
    width: 100%;
    height: 100%;
  }
`;

type SuggestedQuestionItemProps = {
  question: FollowUpQuestion;
  onClick: (question: string) => void;
};

const SuggestedQuestionItem: FC<SuggestedQuestionItemProps> = ({
  question,
  onClick,
}) => {
  return (
    <QuestionCard
      onClick={() => onClick(question.question)}
      aria-label={`Ask: ${question.question}`}
    >
      <QuestionText>
        {question.question}
      </QuestionText>
      <QuestionIcon>
        <ArrowRight />
      </QuestionIcon>
    </QuestionCard>
  );
};

type SuggestedQuestionsProps = {
  questions: FollowUpQuestion[];
  onQuestionClick: (question: string) => void;
};

export const SuggestedQuestions: FC<SuggestedQuestionsProps> = ({
  questions,
  onQuestionClick,
}) => {
  return (
    <QuestionGrid>
      {questions.map(question => (
        <SuggestedQuestionItem
          key={question.question}
          question={question}
          onClick={onQuestionClick}
        />
      ))}
    </QuestionGrid>
  );
};
